import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImportStep from './ImportStep';

// Mirrors MAX_REPORT_SIZE_BYTES in the API's disputeReportUpload middleware.
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export default function AdyenDisputeImportDialog({
  accountId,
  accountName,
  onImport,
  onClose,
}: {
  accountId: number;
  accountName: string;
  onImport: (accountId: number, file: File) => Promise<void>;
  onClose: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const handlePick = (picked: File | null) => {
    if (picked && picked.size > MAX_FILE_SIZE_BYTES) {
      setFile(null);
      setSizeError(
        `${picked.name} is ${(picked.size / 1024 / 1024).toFixed(0)} MB, over the 25 MB limit. Generate the report for a shorter date range.`,
      );
      return;
    }
    setSizeError(null);
    setFile(picked);
  };

  const handleImport = async () => {
    if (!file) return;
    setBusy(true);
    const imported = await onImport(accountId, file).then(() => true, () => false);
    setBusy(false);
    if (imported) {
      onClose();
    }
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || busy) return;
        onClose();
      }}
    >
      <DialogTitle>Import Adyen dispute report — {accountName}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Import old disputes by uploading a dispute report from the Adyen Customer Area.
        </DialogContentText>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
          <ImportStep n={1}>
            In <strong>Reports</strong>, open{' '}
            <strong>Dispute transaction details report (Multiday)</strong>.
          </ImportStep>
          <ImportStep n={2}>
            On the <strong>Column configuration</strong> tab, the simplest option is to tick{' '}
            <strong>every column</strong>, then <strong>Save Configuration</strong>. If you would
            rather be selective, keep the default columns and add the optional{' '}
            <strong>RDR</strong>, <strong>Status</strong> and <strong>Metadata</strong>. A report
            missing a column we read is rejected, and the error names it.
          </ImportStep>
          <ImportStep n={3}>
            Select <strong>Generate</strong>, choose a <strong>Date range</strong>, and generate
            as <strong>.CSV</strong>. We set no hard limit, but{' '}
            <strong>60–90 days is recommended</strong>.
          </ImportStep>
          <ImportStep n={4}>
            Once it is ready, download it from <strong>Generated reports</strong> and upload it below.
          </ImportStep>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Disputes already in the system are only updated when the report is more recent than
          what we hold, so overlapping reports are safe to upload in any order.
        </Typography>

        {sizeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {sizeError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            component="label"
            variant="outlined"
            disabled={busy}
            startIcon={<UploadFileIcon />}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Choose CSV
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => {
                handlePick(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
            />
          </Button>
          <Typography
            variant="body2"
            color={file ? 'text.primary' : 'text.secondary'}
            title={file?.name}
            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {file ? file.name : 'No file selected'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!file || busy}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
        >
          {busy ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
